/**
 * QuoteSelectionPanel — shared selection UI for options and alternates.
 *
 * Two mounts:
 *   1. RecordDecisionPage (mode='internal') — contractor selects which options/alternates to include in the job
 *   2. DocumentReviewSurface → SignaturePage (mode='customer') — customer selects before signing
 *
 * Invariant: same quote, both modes → identical selections, identical totals.
 * The only differences between modes are copy strings.
 *
 * A2 (2026-08-06): added `showBaseLines` prop — renders base items as
 * checked + disabled (locked) so the scope block is visually complete.
 *
 * Money via readEngineLine — no arithmetic in the render path.
 *
 * DEC-49 · Closes DEF-003 + DEF-004 · Unblocks DEF-073
 * Registered in COMPONENT_MAP with both consumers.
 *
 * @module components/shared/QuoteSelectionPanel
 */
import React, { useMemo } from 'react';
import { Card } from '../ui/Card';
import { readEngineLine } from '../../quote-builder/readEngineLine';
import { formatCurrency } from '../../utils/formatCurrency';
import { isOptionLine } from '../../quote-builder/separateOptions';
import { optionDisplayParts } from '../../quote-builder/optionDisplayParts';

/**
 * @typedef {'internal' | 'customer'} SelectionMode
 *
 * @typedef {Object} AlternateSelection
 * @property {string} group_id
 * @property {string} chosen_line_id
 *
 * @typedef {Object} OptionSelection
 * @property {string} line_id
 * @property {boolean} accepted
 *
 * @typedef {Object} Selections
 * @property {AlternateSelection[]} alternates
 * @property {OptionSelection[]} options
 */

const COPY = {
  internal: {
    baseLinesHeading: 'Base Scope (included)',
    optionsHeading: 'Include Options',
    alternatesHeading: 'Select Alternates',
    totalLabel: 'Job Total',
    emptyOptions: 'No optional items on this quote.',
  },
  customer: {
    baseLinesHeading: 'Included Items',
    optionsHeading: 'Optional Items',
    // IDI-131 (D-08, co-UAT 10-13): no "Choose your preference" heading; each
    // alternate group carries its own "Alternate pricing — <area> · <sub-area>"
    // label instead. Copy is a Portica default.
    alternatesHeading: null,
    totalLabel: 'Your Total',
    emptyOptions: 'No optional items available.',
  },
};

/**
 * Get the line total for a line item via readEngineLine.
 * Falls back to lineTotal on the wire if engine returns 0.
 */
function getLineTotal(li) {
  if (!li) return 0;
  try {
    const engine = readEngineLine(li);
    if (engine.total) return engine.total;
  } catch {
    // readEngineLine may fail on minimal test fixtures
  }
  // Fallback: use lineTotal directly from wire data
  return li.lineTotal ?? li.line_total ?? li.total ?? 0;
}

/**
 * Compute the running total based on current selections.
 * Uses readEngineLine for each line item — no arithmetic in this component.
 *
 * @param {Array} lineItems - All line items on the quote
 * @param {Selections} selections - Current selection state
 * @returns {number} Total in cents
 */
function computeSelectionTotal(lineItems, selections) {
  if (!lineItems || lineItems.length === 0) return 0;

  let total = 0;

  for (const li of lineItems) {
    const lineId = li.id || li.line_id;
    const lineTotal = getLineTotal(li);

    // Check if this line is an option
    const optionEntry = selections.options?.find((o) => o.line_id === lineId);
    if (optionEntry) {
      // Only include if accepted
      if (optionEntry.accepted) total += lineTotal;
      continue;
    }

    // Check if this line belongs to an alternate group
    const groupId = li.alternate_group_id || li.alternateGroupId;
    if (groupId) {
      // Find the selection for this group
      const altEntry = selections.alternates?.find((a) => a.group_id === groupId);
      if (altEntry) {
        // Only include if this is the chosen line
        if (altEntry.chosen_line_id === lineId) {
          total += lineTotal;
        }
        // Skip unchosen alternates
        continue;
      }
    }

    // Base line item — always included
    total += lineTotal;
  }

  return total;
}

/**
 * QuoteSelectionPanel — renders option checkboxes and alternate radio groups
 * with a running total that reconciles at every toggle.
 *
 * @param {Object} props
 * @param {Array} props.lineItems - All line items on the quote
 * @param {Selections} props.selections - Current selection state
 * @param {function} props.onSelectionChange - Callback when selections change
 * @param {SelectionMode} [props.mode='internal'] - Governs copy only, never logic
 * @param {boolean} [props.readOnly=false] - Disable interaction (for review states)
 * @param {boolean} [props.showTotal=true] - Show the running total
 * @param {boolean} [props.showBaseLines=false] - Show base lines as locked checkboxes (A2 scope block)
 * @param {boolean} [props.hideAlternates=false] - IDI-131: the host renders each alternate
 *   under its parent line itself (SignaturePage), so the panel must not repeat them.
 */
export default function QuoteSelectionPanel({
  lineItems = [],
  selections = { alternates: [], options: [] },
  onSelectionChange,
  mode = 'internal',
  readOnly = false,
  showTotal = true,
  showBaseLines = false,
  hideAlternates = false,
}) {
  const copy = COPY[mode] || COPY.internal;

  const hasOptions = selections.options && selections.options.length > 0;
  const hasAlternates = !hideAlternates && selections.alternates && selections.alternates.length > 0;
  const hasSelections = hasOptions || hasAlternates;

  const runningTotal = useMemo(
    () => computeSelectionTotal(lineItems, selections),
    [lineItems, selections]
  );

  // A2: derive base lines (neither option nor alternate)
  const baseLines = useMemo(() => {
    if (!showBaseLines) return [];
    const optionIds = new Set((selections.options || []).map((o) => o.line_id));
    const alternateGroupIds = new Set((selections.alternates || []).map((a) => a.group_id));
    return lineItems.filter((li) => {
      const lineId = li.id || li.line_id;
      // Exclude if it's an option (by selection OR by any option marker)
      if (optionIds.has(lineId)) return false;
      if (isOptionLine(li)) return false;
      // Exclude if it's an alternate (by group membership OR by flag)
      if (li.is_alternate || li.isAlternate) return false;
      const groupId = li.alternate_group_id || li.alternateGroupId;
      if (groupId && alternateGroupIds.has(groupId)) return false;
      return true;
    });
  }, [showBaseLines, lineItems, selections.options, selections.alternates]);

  if (!hasSelections && baseLines.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="quote-selection-panel">
      {/* A2: Base lines — always included, locked (S-4: cannot be deselected) */}
      {baseLines.length > 0 && (
        <BaseLineItems baseLines={baseLines} heading={copy.baseLinesHeading} />
      )}

      {hasAlternates && (
        <AlternateSelectors
          alternates={selections.alternates}
          lineItems={lineItems}
          onChange={(updated) => onSelectionChange?.({ ...selections, alternates: updated })}
          heading={copy.alternatesHeading}
          readOnly={readOnly}
        />
      )}

      {hasOptions && (
        <OptionSelectors
          options={selections.options}
          lineItems={lineItems}
          onChange={(updated) => onSelectionChange?.({ ...selections, options: updated })}
          heading={copy.optionsHeading}
          readOnly={readOnly}
        />
      )}

      {showTotal && (
        <div
          className="flex items-center justify-between px-4 py-3 rounded-lg"
          style={{ background: 'var(--color-surface-sunken)', border: '1px solid var(--color-border)' }}
          data-testid="selection-running-total"
        >
          <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
            {copy.totalLabel}
          </span>
          <span className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {formatCurrency(runningTotal)}
          </span>
        </div>
      )}
    </div>
  );
}

// --- IDI-362: customer-legible line labels ---
// Sarah: "every line shows its area and sub-area, or its Alias", the same as
// every other quote screen. Alias (IDI-114) replaces the product name.
export function lineWhere(li) {
  const area = li?.areaLabel || li?.area_label || '';
  const sub = li?.subAreaLabel || li?.sub_area_label || '';
  return [area, sub].filter(Boolean).join(' — ');
}

export function lineName(li) {
  // IDI-359 (P-18): a whitespace-only alias counts as unset.
  const alias = typeof li?.alias === 'string' ? li.alias.trim() : '';
  return alias || li?.item_name || li?.itemName || li?.productLabel || li?.name || li?.description || 'Unnamed item';
}

function LineLabel({ li }) {
  const where = lineWhere(li);
  const note = li?.notes || '';
  return (
    <span className="min-w-0">
      {where && (
        <span className="text-xs block" style={{ color: 'var(--color-text-muted)' }}>{where}</span>
      )}
      <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{lineName(li)}</span>
      {note && (
        <span className="text-xs block" style={{ color: 'var(--color-text-muted)' }}>{note}</span>
      )}
    </span>
  );
}

// IDI-131 (D-08): "Alternate pricing — Ceilings · Inaccessible". The area
// and sub-area come from the alternate, falling back to its parent line.
export function alternatePricingLabel(alt, parent) {
  const area = alt?.areaLabel || alt?.area_label || parent?.areaLabel || parent?.area_label || '';
  const sub = alt?.subAreaLabel || alt?.sub_area_label || parent?.subAreaLabel || parent?.sub_area_label || '';
  const where = [area, sub].filter(Boolean).join(' · ');
  return where ? `Alternate pricing — ${where}` : 'Alternate pricing';
}

// IDI-545 (Adrian 10-21): an option reads "<sub-area heading> — <alias>",
// then the product on its own line, then the line note — the same order as
// Review & Send and the PDF (optionDisplayParts).
function OptionLabel({ li }) {
  // The raw line goes straight to optionDisplayParts so the product resolves
  // with the same fallback order as Review & Send and the PDF (optionProduct).
  const { title, product } = optionDisplayParts(li || {});
  const note = li?.notes || li?.note || '';
  return (
    <span className="min-w-0 text-sm" style={{ color: 'var(--color-text-primary)' }}>
      {title && <span className="block" data-testid="signing-option-title">{title}</span>}
      {product && <span className="block" data-testid="signing-option-product">{product}</span>}
      {note && (
        <span className="text-xs block" style={{ color: 'var(--color-text-muted)' }} data-testid="signing-option-note">{note}</span>
      )}
    </span>
  );
}

export function fmtSigned(cents) {
  const n = Math.round(Number(cents) || 0);
  if (n === 0) return 'no change';
  return `${n < 0 ? '−' : '+'}${formatCurrency(Math.abs(n))}`;
}

// --- Internal sub-components ---

/**
 * BaseLineItems — A2 scope block: renders base lines as checked + disabled.
 * These are always included in the job; the customer cannot deselect them (S-4).
 */
function BaseLineItems({ baseLines, heading }) {
  return (
    <Card className="p-4">
      <h4 className="text-sm font-medium text-gray-700 mb-2">{heading}</h4>
      {baseLines.map((li) => {
        const lineId = li.id || li.line_id;
        const lineTotal = getLineTotal(li);
        return (
          <label
            key={lineId}
            data-testid="signing-base-line"
            className="flex items-start gap-2 py-1 opacity-80 cursor-not-allowed"
          >
            <input
              type="checkbox"
              checked
              disabled
              className="mt-1"
              style={{ accentColor: 'var(--color-action-primary)' }}
            />
            <LineLabel li={li} />
            {lineTotal ? (
              <span className="ml-auto text-sm tabular-nums" style={{ color: 'var(--color-text-default)' }}>
                {formatCurrency(lineTotal)}
              </span>
            ) : null}
          </label>
        );
      })}
    </Card>
  );
}

function AlternateSelectors({ alternates, lineItems, onChange, heading, readOnly }) {
  // For each alternate group, find ALL line items that belong to it.
  const groups = useMemo(() => {
    return alternates.map((alt) => {
      const groupLines = lineItems.filter(
        (li) => (li.alternate_group_id || li.alternateGroupId) === alt.group_id
      );
      // IDI-362: the parent is the group's own line (its id IS the group id).
      const parent = groupLines.find((li) => (li.id || li.line_id) === alt.group_id) || groupLines[0];
      return {
        group_id: alt.group_id,
        chosen_line_id: alt.chosen_line_id,
        group_label: alt.group_label || groupLines[0]?.alternate_group_label || `Group ${alt.group_id}`,
        parent,
        lines: groupLines,
      };
    });
  }, [alternates, lineItems]);

  function handleChoose(groupId, chosenLineId) {
    if (readOnly) return;
    const updated = alternates.map((a) =>
      a.group_id === groupId ? { ...a, chosen_line_id: chosenLineId } : a
    );
    onChange(updated);
  }

  return (
    <Card className="p-4">
      {heading && (
        <h4 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text-primary)' }}>
          {heading}
        </h4>
      )}
      {groups.map((group) => (
        <fieldset key={group.group_id} className="mb-3 last:mb-0">
          <legend className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }} data-testid="signing-alternate-legend">
            {/* IDI-131 (D-08): each group is clearly labelled as alternate
                pricing for a named place, e.g. "Alternate pricing — Ceilings ·
                Inaccessible"; the parent row below is marked "as quoted". */}
            {group.parent
              ? alternatePricingLabel(group.lines.find((l) => l !== group.parent), group.parent)
              : group.group_label}
          </legend>
          {group.lines.map((li) => {
            const lineId = li.id || li.line_id;
            const isChosen = group.chosen_line_id === lineId;
            const lineTotal = getLineTotal(li);
            return (
              <label
                key={lineId}
                className="flex items-start gap-2 py-1.5 cursor-pointer"
                style={{ opacity: readOnly ? 0.7 : 1 }}
              >
                <input
                  type="radio"
                  name={`alt-group-${group.group_id}`}
                  checked={isChosen}
                  onChange={() => handleChoose(group.group_id, lineId)}
                  disabled={readOnly}
                  style={{ accentColor: 'var(--color-action-primary)' }}
                />
                <LineLabel li={li} />
                {(() => {
                  const isParent = group.parent && lineId === (group.parent.id || group.parent.line_id);
                  if (isParent) {
                    return (
                      <span className="ml-auto text-sm tabular-nums" style={{ color: 'var(--color-text-default)' }}>
                        {lineTotal ? formatCurrency(lineTotal) : ''}
                        <span className="text-xs block text-right" style={{ color: 'var(--color-text-muted)' }}>as quoted</span>
                      </span>
                    );
                  }
                  // RULED 2026-09-29: the signed Cost Add/Deduct vs the parent,
                  // with the absolute price as secondary text (signing page only).
                  const delta = lineTotal - (group.parent ? getLineTotal(group.parent) : 0);
                  return (
                    <span className="ml-auto text-sm tabular-nums" style={{ color: 'var(--color-text-default)' }} data-testid="signing-alternate-delta">
                      {fmtSigned(delta)}
                      {lineTotal ? (
                        <span className="text-xs block text-right" style={{ color: 'var(--color-text-muted)' }}>{formatCurrency(lineTotal)}</span>
                      ) : null}
                    </span>
                  );
                })()}
              </label>
            );
          })}
        </fieldset>
      ))}
    </Card>
  );
}

function OptionSelectors({ options, lineItems, onChange, heading, readOnly }) {
  function handleToggle(lineId) {
    if (readOnly) return;
    const updated = options.map((o) =>
      o.line_id === lineId ? { ...o, accepted: !o.accepted } : o
    );
    onChange(updated);
  }

  if (options.length === 0) return null;

  return (
    <Card className="p-4">
      <h4 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text-primary)' }}>
        {heading}
      </h4>
      {options.map((opt) => {
        const li = lineItems.find((l) => (l.id || l.line_id) === opt.line_id);
        const lineTotal = getLineTotal(li);
        return (
          <label
            key={opt.line_id}
            data-testid="signing-option-line"
            className="flex items-start gap-2 py-1.5 cursor-pointer"
            style={{ opacity: readOnly ? 0.7 : 1 }}
          >
            <input
              type="checkbox"
              checked={opt.accepted}
              onChange={() => handleToggle(opt.line_id)}
              disabled={readOnly}
              style={{ accentColor: 'var(--color-action-primary)' }}
            />
            {/* IDI-362: same label as every other line: area — sub-area, then
                alias || product name (lineName). The old chain read
                alias || item_name || description and IGNORED productLabel, which is
                the field the signature projection sends, so the customer saw
                "Unnamed item". Terminal fallback stays 'Unnamed item', never a raw
                id (IDI-71). IDI-545: heading — alias / product / note. */}
            <OptionLabel li={li} />
            {lineTotal ? (
              <span className="ml-auto text-sm tabular-nums" style={{ color: 'var(--color-text-default)' }}>
                {formatCurrency(lineTotal)}
              </span>
            ) : null}
          </label>
        );
      })}
    </Card>
  );
}

// Export for testing
export { computeSelectionTotal };
